/**
 * Willtec Cart Drawer Custom Element
 * Handles opening, closing, quantity changes via AJAX (/cart/change.js),
 * item removal, race-condition handling, accessibility and live subtotal/total updates.
 */
(() => {
  if (customElements.get('willtec-cart-drawer')) return;

  class WilltecCartDrawer extends HTMLElement {
  constructor() {
    super();
    this.isUpdating = false;
    this.debounceTimers = new Map();
    this.pendingQuantities = new Map();
    this.queue = [];
    this.isProcessingQueue = false;
  }

  connectedCallback() {
    if (!this.initialized) {
      this.bindPermanentEvents();
      this.initialized = true;
    }
    this.bindCartActions();
  }

  bindPermanentEvents() {
    // Persistent document listeners registered once
    this.handleRefresh = () => this.refreshCart();
    this.handleOpen = () => this.open();

    document.addEventListener('cart:refresh', this.handleRefresh);
    document.addEventListener('cart:open', this.handleOpen);
    document.addEventListener('willtec:cart:open', this.handleOpen);

    // Delegation on root Custom Element persistence
    this.onclick = (e) => {
      // Close button clicked
      if (e.target.closest('[data-cart-close]')) {
        e.preventDefault();
        this.close();
        return;
      }

      // Overlay or backdrop clicked
      if (e.target === this || e.target === this.overlay || e.target.hasAttribute('data-cart-overlay')) {
        this.close();
        return;
      }
    };

    // Attach click events to all cart drawer triggers on the page
    document.querySelectorAll('[aria-controls="WilltecCartDrawer"]').forEach((trigger) => {
      trigger.onclick = (e) => {
        e.preventDefault();
        this.open();
      };
    });
  }

  disconnectedCallback() {
    if (this.handleRefresh) document.removeEventListener('cart:refresh', this.handleRefresh);
    if (this.handleOpen) {
      document.removeEventListener('cart:open', this.handleOpen);
      document.removeEventListener('willtec:cart:open', this.handleOpen);
    }
  }

  bindEvents() {
    // Method called after innerHTML updates to refresh references & cart actions
    this.dialog = this.querySelector('dialog') || this;
    this.closeBtn = this.querySelector('[data-cart-close]');
    this.overlay = this.querySelector('[data-cart-overlay]');

    if (this.dialog) {
      this.dialog.onclick = (e) => {
        if (e.target.closest('[data-cart-close]')) {
          e.preventDefault();
          this.close();
          return;
        }

        const rect = this.dialog.getBoundingClientRect();
        const isInDialog = (
          rect.top <= e.clientY &&
          e.clientY <= rect.top + rect.height &&
          rect.left <= e.clientX &&
          e.clientX <= rect.left + rect.width
        );
        if (!isInDialog) {
          this.close();
        }
      };

      this.dialog.oncancel = () => this.close();
    }

    this.bindCartActions();
  }

  bindCartActions() {
    const handleQtyChange = (btn, delta) => {
      const key = btn.getAttribute('data-key');
      if (!key) return;

      const itemCard = this.querySelector(`[data-cart-item="${key}"]`);
      const minusBtn = itemCard ? itemCard.querySelector('[data-cart-minus]') : this.querySelector(`[data-cart-minus][data-key="${key}"]`);
      const plusBtn = itemCard ? itemCard.querySelector('[data-cart-plus]') : this.querySelector(`[data-cart-plus][data-key="${key}"]`);
      const qtyDisplay = itemCard ? itemCard.querySelector('[data-cart-qty-display]') : btn.parentElement?.querySelector('[data-cart-qty-display]');

      // Read current quantity: prioritize pending count if user is clicking repeatedly
      const currentQty = this.pendingQuantities.has(key)
        ? this.pendingQuantities.get(key)
        : parseInt(btn.getAttribute('data-qty') || '1', 10);

      const newQty = Math.max(0, currentQty + delta);
      this.pendingQuantities.set(key, newQty);

      // Instant optimistic UI update (zero delay, buttons stay clickable)
      if (qtyDisplay) {
        const formatted = (newQty < 10 ? '0' : '') + newQty;
        if (qtyDisplay.tagName === 'INPUT') {
          qtyDisplay.value = formatted;
        } else {
          qtyDisplay.textContent = formatted;
        }
        qtyDisplay.setAttribute('data-qty', String(newQty));
      }
      if (minusBtn) minusBtn.setAttribute('data-qty', String(newQty));
      if (plusBtn) plusBtn.setAttribute('data-qty', String(newQty));

      if (itemCard) {
        if (newQty === 0) {
          itemCard.classList.add('opacity-40');
        } else {
          itemCard.classList.remove('opacity-40');
        }
      }

      // Reset debounce timer (500ms after last click to commit request)
      if (this.debounceTimers.has(key)) {
        clearTimeout(this.debounceTimers.get(key));
      }

      const timer = setTimeout(() => {
        this.debounceTimers.delete(key);
        this.enqueueUpdate(key, newQty);
      }, 500);

      this.debounceTimers.set(key, timer);
    };

    // Quantity decrease (-) buttons
    this.querySelectorAll('[data-cart-minus]').forEach((btn) => {
      btn.onclick = (e) => {
        e.preventDefault();
        handleQtyChange(btn, -1);
      };
    });

    // Quantity increase (+) buttons
    this.querySelectorAll('[data-cart-plus]').forEach((btn) => {
      btn.onclick = (e) => {
        e.preventDefault();
        handleQtyChange(btn, 1);
      };
    });

    // Direct quantity input handling
    this.querySelectorAll('input[data-cart-qty-display]').forEach((input) => {
      const key = input.getAttribute('data-key');
      if (!key) return;

      const itemCard = this.querySelector(`[data-cart-item="${key}"]`);
      const minusBtn = itemCard ? itemCard.querySelector('[data-cart-minus]') : this.querySelector(`[data-cart-minus][data-key="${key}"]`);
      const plusBtn = itemCard ? itemCard.querySelector('[data-cart-plus]') : this.querySelector(`[data-cart-plus][data-key="${key}"]`);

      // Select all text on focus for effortless editing
      input.addEventListener('focus', () => {
        input.select();
      });

      // While typing: update pending state and debounce API update
      input.addEventListener('input', () => {
        const raw = input.value.trim();
        if (raw === '') return;

        const newQty = parseInt(raw, 10);
        if (isNaN(newQty) || newQty < 0) return;

        this.pendingQuantities.set(key, newQty);
        input.setAttribute('data-qty', String(newQty));
        if (minusBtn) minusBtn.setAttribute('data-qty', String(newQty));
        if (plusBtn) plusBtn.setAttribute('data-qty', String(newQty));

        if (itemCard) {
          if (newQty === 0) {
            itemCard.classList.add('opacity-40');
          } else {
            itemCard.classList.remove('opacity-40');
          }
        }

        if (this.debounceTimers.has(key)) {
          clearTimeout(this.debounceTimers.get(key));
        }

        const timer = setTimeout(() => {
          this.debounceTimers.delete(key);
          this.enqueueUpdate(key, newQty);
        }, 500);

        this.debounceTimers.set(key, timer);
      });

      const commitInput = () => {
        let val = parseInt(input.value.trim(), 10);
        if (isNaN(val) || val < 0) {
          val = 1;
        }

        const formatted = (val < 10 ? '0' : '') + val;
        input.value = formatted;
        input.setAttribute('data-qty', String(val));
        if (minusBtn) minusBtn.setAttribute('data-qty', String(val));
        if (plusBtn) plusBtn.setAttribute('data-qty', String(val));

        const pending = this.pendingQuantities.get(key);
        if (pending !== val) {
          this.pendingQuantities.set(key, val);
          if (this.debounceTimers.has(key)) {
            clearTimeout(this.debounceTimers.get(key));
            this.debounceTimers.delete(key);
          }
          this.enqueueUpdate(key, val);
        }
      };

      input.addEventListener('blur', commitInput);
      input.addEventListener('change', commitInput);

      input.addEventListener('keydown', (e) => {
        if (['e', 'E', '+', '-', '.'].includes(e.key)) {
          e.preventDefault();
          return;
        }
        if (e.key === 'Enter') {
          e.preventDefault();
          commitInput();
          input.blur();
        }
      });
    });

    // Remove item (trash) buttons
    this.querySelectorAll('[data-cart-remove]').forEach((btn) => {
      btn.onclick = (e) => {
        e.preventDefault();
        const key = btn.getAttribute('data-key');
        if (!key) return;

        if (this.debounceTimers.has(key)) {
          clearTimeout(this.debounceTimers.get(key));
          this.debounceTimers.delete(key);
        }
        this.pendingQuantities.set(key, 0);

        const itemCard = this.querySelector(`[data-cart-item="${key}"]`);
        if (itemCard) itemCard.classList.add('opacity-40');

        this.enqueueUpdate(key, 0);
      };
    });
  }

  open() {
    this.classList.remove('hidden');
    this.style.display = 'block';

    const dialog = this.querySelector('dialog');
    if (dialog) {
      dialog.classList.remove('hidden');
      if (typeof dialog.showModal === 'function') {
        try {
          if (!dialog.open) dialog.showModal();
        } catch (e) {
          dialog.setAttribute('open', '');
        }
      } else {
        dialog.setAttribute('open', '');
      }
    }

    document.body.style.overflow = 'hidden';
    
    // Set ARIA on triggers
    document.querySelectorAll('[aria-controls="WilltecCartDrawer"]').forEach((t) => {
      t.setAttribute('aria-expanded', 'true');
    });

    if (this.closeBtn) this.closeBtn.focus();
  }

  close() {
    const dialog = this.querySelector('dialog');
    if (dialog) {
      if (typeof dialog.close === 'function') {
        try {
          if (dialog.open) dialog.close();
        } catch (e) {
          dialog.removeAttribute('open');
        }
      } else {
        dialog.removeAttribute('open');
      }
    }

    this.style.display = 'none';
    this.classList.add('hidden');
    document.body.style.overflow = '';

    document.querySelectorAll('[aria-controls="WilltecCartDrawer"]').forEach((t) => {
      t.setAttribute('aria-expanded', 'false');
    });
  }

  enqueueUpdate(key, quantity) {
    this.queue = this.queue || [];
    const existingIndex = this.queue.findIndex((item) => item.key === key);
    if (existingIndex >= 0) {
      this.queue[existingIndex].quantity = quantity;
    } else {
      this.queue.push({ key, quantity });
    }

    if (!this.isProcessingQueue) {
      this.processQueue();
    }
  }

  async processQueue() {
    if (this.isProcessingQueue) return;
    this.isProcessingQueue = true;

    while (this.queue && this.queue.length > 0) {
      const item = this.queue.shift();
      const itemCard = this.querySelector(`[data-cart-item="${item.key}"]`);
      if (itemCard && item.quantity > 0) {
        itemCard.classList.add('opacity-70');
      }

      try {
        const response = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            id: item.key,
            quantity: item.quantity
          })
        });

        if (!response.ok) {
          throw new Error('Falha ao atualizar o carrinho');
        }
      } catch (error) {
        console.error('Willtec Cart Error:', error);
      } finally {
        if (itemCard) itemCard.classList.remove('opacity-70');
        this.pendingQuantities.delete(item.key);
      }
    }

    // Refresh cart when queue finishes and no active debounce timers remain
    if (this.debounceTimers.size === 0) {
      await this.refreshCart();
    }

    this.isProcessingQueue = false;

    // Check if new items were queued while running
    if (this.queue && this.queue.length > 0) {
      this.processQueue();
    }
  }

  updateQuantity(key, quantity) {
    if (this.debounceTimers.has(key)) {
      clearTimeout(this.debounceTimers.get(key));
      this.debounceTimers.delete(key);
    }
    this.pendingQuantities.delete(key);
    this.enqueueUpdate(key, quantity);
  }

  async refreshCart() {
    try {
      const res = await fetch(`${window.location.pathname}?sections=willtec-cart-drawer,header`);
      const data = await res.json();

      if (data['willtec-cart-drawer']) {
        const parser = new DOMParser();
        const htmlDoc = parser.parseFromString(data['willtec-cart-drawer'], 'text/html');
        const newDrawerContent = htmlDoc.querySelector('willtec-cart-drawer');
        if (newDrawerContent) {
          this.innerHTML = newDrawerContent.innerHTML;
          this.bindEvents();
          if (window.FiscalCheckoutGuard) {
            window.FiscalCheckoutGuard.updateFiscalSummaries();
          }
        }
      }

      if (data['header']) {
        const parser = new DOMParser();
        const headerDoc = parser.parseFromString(data['header'], 'text/html');
        
        // Update header cart counts
        const newHeaderCounts = headerDoc.querySelectorAll('.willtec-cart-count');
        const currentHeaderCounts = document.querySelectorAll('.willtec-cart-count');

        currentHeaderCounts.forEach((countEl, idx) => {
          if (newHeaderCounts[idx]) {
            countEl.innerHTML = newHeaderCounts[idx].innerHTML;
          }
        });
      }
    } catch (err) {
      console.error('Error refreshing cart section:', err);
      // Fallback reload if section rendering API fails
      window.location.reload();
    }
  }
}

  if (!customElements.get('willtec-cart-drawer')) {
    customElements.define('willtec-cart-drawer', WilltecCartDrawer);
  }
})();
